import { Badge, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { qaCheckName, readVisitQa, type QaCheckStatus, type QaVerdict } from '../../lib/qaChecks';
import type { VisitQa } from '../../types/api';
import { DetailSection } from './DetailSection';

const VERDICT_COLOR: Record<QaVerdict, string> = {
    checks_passed: 'green',
    needs_attention: 'orange',
    failed: 'red',
    not_run: 'gray',
};

const STATUS_COLOR: Record<QaCheckStatus, string> = {
    failed: 'red',
    unmeasured: 'gray',
    passed: 'green',
    informational: 'blue',
};

/** The automated QA checks the backend ran on this visit's report. */
export function QaChecksSection({ qa }: { qa: VisitQa | null | undefined }) {
    const { t } = useTranslation();
    const { verdict, checks } = readVisitQa(qa);

    return (
        <DetailSection title={t('features.visits.qa.title')} description={t('features.visits.qa.description')}>
            <div>
                <Badge color={VERDICT_COLOR[verdict]} variant="light">
                    {t(`features.visits.qa.verdicts.${verdict}`)}
                </Badge>
            </div>
            {checks.length === 0 ? (
                <Text size="sm" c="dimmed">
                    {t('features.visits.qa.none')}
                </Text>
            ) : (
                <Table striped withTableBorder>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>{t('features.visits.qa.check')}</Table.Th>
                            <Table.Th>{t('features.visits.qa.result')}</Table.Th>
                            <Table.Th>{t('features.visits.qa.reason')}</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {checks.map((c) => (
                            <Table.Tr key={c.key}>
                                <Table.Td>{qaCheckName(t, c.key)}</Table.Td>
                                <Table.Td>
                                    <Badge color={STATUS_COLOR[c.status]} variant="light">
                                        {t(`features.visits.qa.statuses.${c.status}`)}
                                    </Badge>
                                </Table.Td>
                                <Table.Td>{c.reason ?? t('common.empty')}</Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            )}
        </DetailSection>
    );
}
